import { postForm } from '@/shared/api/client';
import { submitForm } from '@/shared/api/submit';
import { getListRows } from '@/shared/lib/pagination';
import type { PlaceItem } from '@/shared/api/places';

export type ListResponse = Record<string, unknown>;

export interface PlaceListParams {
  page: number;
  sort_field: string;
  sort_order: string;
  id: string;
  name: string;
}

export interface Country extends Record<string, unknown> {
  id: number;
  name: string;
  phone_code?: string | null;
  lang?: string;
}

export interface Region extends Record<string, unknown> {
  id: number;
  name: string;
  country_id?: number;
  country?: { id: number; name?: string } | null;
}

export interface City extends Record<string, unknown> {
  id: number;
  name: string;
  country_id?: number;
  region_id?: number;
  country?: { id: number; name?: string } | null;
  region?: { id: number; name?: string } | null;
}

export interface CountryFeature {
  feature_id: number;
  is_available: boolean;
  transliterate: string;
  description: string;
}

/** All regions (optionally of one country) — legacy `getRegions(token, countryId || 0)`. */
export async function fetchRegionOptions(countryId?: string) {
  const res = await postForm<ListResponse>('/place/region/list/', {
    country_id: countryId || 0,
    limit: 10000,
  });
  return getListRows<PlaceItem>(res, ['regions']);
}

export const fetchCountriesList = (params: PlaceListParams) =>
  postForm<ListResponse>('/place/country/list/', { ...params });

export const fetchRegionsList = (params: PlaceListParams & { country_id: string }) =>
  postForm<ListResponse>('/place/region/list/', { ...params });

export const fetchCitiesList = (params: PlaceListParams & { country_id: string; region_id: string }) =>
  postForm<ListResponse>('/place/city/list/', { ...params });

export async function fetchCity(id: number) {
  const res = await postForm<{ city: City }>('/place/city/view/', { id });
  return res.city;
}

export async function fetchRegion(id: number) {
  const res = await postForm<{ region: Region }>('/place/region/view/', { id });
  return res.region;
}

export const saveCity = ({ id, name, regionId }: { id?: number; name: string; regionId: string }) =>
  submitForm(id ? '/place/city/edit/' : '/place/city/create/', { id, name, region_id: regionId });

export const saveRegion = ({ id, name, countryId }: { id?: number; name: string; countryId: string }) =>
  submitForm(id ? '/place/region/edit/' : '/place/region/create/', {
    id,
    name,
    country_id: countryId,
  });

export const saveCountry = ({ id, name, phoneCode }: { id?: number; name: string; phoneCode: string }) =>
  submitForm(
    id ? '/place/country/edit/' : '/place/country/create/',
    id ? { id, name, phone_code: phoneCode } : { name },
  );

export async function fetchCountryFeatures(countryId: number) {
  const res = await postForm<{ data?: CountryFeature[] }>('/place/country/get-available-features/', {
    country_id: countryId,
  });
  return res.data ?? [];
}

export const updateCountryFeatures = (countryId: number, featureIds: number[]) =>
  submitForm('/place/country/update-available-features/', {
    country_id: countryId,
    available_features: featureIds,
  });
